import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  NgZone,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatOptionModule } from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MatDialogModule } from '@angular/material/dialog';

import { MenuComponent } from '../../../shared/components/menu/menu.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import { LoginValidationComponent } from '../../../book-create/login-validation.component/login-validation.component';

import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { CategoryService } from '../../../shared/services/apis/platform/category.service';
import { BookPrintService } from '../../../shared/services/apis/platform/book-print.service';
import { FileUploadService } from '../../../shared/services/apis/platform/file-upload.service';
import { AdminBookEditorService } from '../../../shared/services/apis/platform/admin-book-editor.service';
import { PublicSimulatorService } from '../../../shared/services/apis/platform/public-simulator.service';
import { BookCostCalc, getMinPrice } from '../../../my-books/book-publish-edit.component/book-cost.calculator';
import { UserService } from '../../../shared/services/apis/platform/user.service';
import { UserSimpleStruct } from '../../../shared/services/structs/user-list.response';
import { UserRegisterComponent } from '../../../shared/components/user-register/user-register.component';

import { AdminBookEditorEditResponse, StatusVersionItem } from '../../../shared/services/responses/platform/admin-book-editor.response';
import { AdminBookEditorSaveRequest, AdminBookEditorKitSaveRequest } from '../../../shared/services/requests/platform/admin-book-editor.request';
import { FileUploadCreateResponse, FileUploadFinalizeResponse } from '../../../shared/services/responses/platform/file-upload-create.response';
import { UploadPurposeEnum } from '../../../shared/services/enums/upload-purpose.enum';
import { CategoryStruct } from '../../../shared/services/structs/category.struct';
import { BookVersion } from '../../../shared/services/models/platform/book-version.model';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import {
  AdminBookEditorMarketplaceStruct,
  MarketplaceListingStruct,
  MarketplaceSyncLogStruct,
} from '../../../shared/services/structs/marketplace.struct';
import { MarketplaceListingStatusEnum, MarketplaceSyncStatusEnum } from '../../../shared/services/enums/marketplace.enum';
import {
  AdminBookEditorMarketplaceReprocessRequest,
  AdminBookEditorMarketplaceToggleRequest,
} from '../../../shared/services/requests/platform/admin-book-editor.request';
import { AdminBookEditorMarketplaceResponse } from '../../../shared/services/responses/platform/admin-book-editor.response';

type CategoryOptionItem = {
  isHeader: boolean;
  label: string;
  idCategory?: number;
  classKey?: number;
};

type KitEditRow = {
  idKit: number;
  kitName: string | null;
  kitDescription: string | null;
  totalPrice: number;
  isActive: boolean;
  selectedItems: string[];
};

@Component({
  selector: 'app-admin-book-editor-edit',
  standalone: true,
  templateUrl: './admin-book-editor-edit.component.html',
  styleUrls: ['./admin-book-editor-edit.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatOptionModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatDialogModule,
    MatSlideToggleModule,
    MenuComponent,
    FooterComponent,
    LoginValidationComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
})
export class AdminBookEditorEditComponent implements OnInit {
  idBookVersion!: number;
  isLoading = true;
  isSaving = false;

  bookVersion: BookVersion | null = null;

  // ---- Category state ----
  listCategories: CategoryStruct[] = [];
  private allOptions: CategoryOptionItem[] = [];
  primaryOptions: CategoryOptionItem[] = [];
  secondaryOptions: CategoryOptionItem[] = [];

  // ---- Print state ----
  listBookPrint: any[] = [];

  // ---- Upload loading flags ----
  uploadLoading: Record<string, boolean> = {
    authorPhoto: false,
    frontCover: false,
    backCover: false,
    coverForPrint: false,
    bookForPrint: false,
    bookForEbook: false,
  };

  // ---- Image previews ----
  authorPhotoPreview: string | null = null;
  frontCoverPreview: string | null = null;
  backCoverPreview: string | null = null;

  // ---- Existing file URLs from backend ----
  existingAuthorPhotoUrl: string | null = null;
  existingFrontCoverUrl: string | null = null;
  existingBackCoverUrl: string | null = null;
  existingCoverForPrintUrl: string | null = null;
  existingBookForPrintUrl: string | null = null;
  existingEbookUrl: string | null = null;

  // ---- PDF labels ----
  coverForPrintLabel: string | null = null;
  bookForPrintLabel: string | null = null;
  bookForEbookLabel: string | null = null;

  // ---- Kit rows ----
  kitRows: KitEditRow[] = [];

  // ---- Availability flags ----
  hasEbookPdf = false;
  hasPrintPdfs = false;
  hasAnyCourse = false;

  // ---- Book owner / user picker ----
  allUsers: UserSimpleStruct[] = [];
  filteredUsers: UserSimpleStruct[] = [];
  selectedUser: UserSimpleStruct | null = null;
  userSearch = '';
  lockUserSelect = false;
  trackByUser = (_: number, u: UserSimpleStruct) => u?.idUser ?? _;

  // ---- Status ----
  readonly PUBLISHED_STATUS_ID = 9;
  listStatusVersion: StatusVersionItem[] = [];
  selectedStatusId: number | null = null;
  originalStatusId: number | null = null;

  // ---- Marketplace (Amazon) ----
  marketplace: AdminBookEditorMarketplaceStruct | null = null;
  /** Livro sincronizado/em sincronização com marketplace: formulário somente leitura. */
  isLockedByMarketplace = false;
  marketplaceLockReason: string | null = null;
  isTogglingMarketplace = false;
  expandedLogId: number | null = null;
  readonly ListingStatus = MarketplaceListingStatusEnum;
  trackByListing = (_: number, l: MarketplaceListingStruct) => l.idMarketplaceListing;
  trackByLog = (_: number, l: MarketplaceSyncLogStruct) => l.idMarketplaceSyncLog;

  public model!: FormGroup;

  private static readonly MAX_IMAGE_BYTES = 5 * 1024 * 1024;
  private static readonly MAX_PDF_BYTES = 210 * 1024 * 1024;

  private loggedIn = false;
  publicConfig: { platformProfitPercent: number; baseTaxPercent: number; productionCostProfitMarginPercent: number } | null = null;

  constructor(
    private fb: FormBuilder,
    private zone: NgZone,
    private route: ActivatedRoute,
    private router: Router,
    private alert: AlertService,
    private categoryService: CategoryService,
    private bookPrintService: BookPrintService,
    private fileUpload: FileUploadService,
    private service: AdminBookEditorService,
    private userService: UserService,
    private dialog: MatDialog,
    public cdr: ChangeDetectorRef,
    private simulatorService: PublicSimulatorService,
  ) {}

  ngOnInit(): void {
    this.idBookVersion = Number(this.route.snapshot.paramMap.get('idBookVersion'));

    this.model = this.fb.group({
      bookTitle: ['', [Validators.required]],
      coverAuthorName: ['', [Validators.required]],
      bookSubtitle: [''],
      versionName: ['', [Validators.required]],
      collectionName: [''],
      volumeName: [''],
      synopsis: ['', [Validators.required, Validators.minLength(500), Validators.maxLength(800)]],
      preface: ['', [Validators.maxLength(250)]],
      backCoverText: [''],
      isbn: ['', [Validators.required, (c: AbstractControl) => this.isbnValidator(c)]],
      doi: [''],
      idPrimaryCategory: [null, [Validators.required]],
      idSecondaryCategory: [null, [Validators.required]],
      idPhotoShape: [1, [Validators.required]],
      authorBio: ['', [Validators.required, Validators.maxLength(4000)]],
      authorPhotoFileName: [''],
      frontCoverFileName: [''],
      backCoverFileName: [''],
      coverForPrintFileName: [''],
      bookForPrintFileName: [''],
      bookForEbookFileName: [''],
      saleCoverFileName: [''],
      totalPages: [0, [Validators.required, Validators.min(1)]],
      idBookPrint: [null, [Validators.required, Validators.min(1)]],
      listAuthors: this.fb.array([this.fb.control('', Validators.required)]),
    });

    this.model.get('idPrimaryCategory')?.valueChanges.subscribe(() => this.refreshCategoryOptions());
    this.model.get('idSecondaryCategory')?.valueChanges.subscribe(() => this.refreshCategoryOptions());

    this.model.get('totalPages')!.valueChanges.subscribe((pages: number) => {
      if (!pages || pages <= 0) return;
      this.loadBookPrints(pages);
    });

    this.model.get('bookForPrintFileName')!.valueChanges.subscribe(() => this.updateAvailability());
    this.model.get('coverForPrintFileName')!.valueChanges.subscribe(() => this.updateAvailability());
    this.model.get('bookForEbookFileName')!.valueChanges.subscribe(() => this.updateAvailability());
  }

  onLoginValidated(isLogged: boolean): void {
    this.loggedIn = isLogged;
    if (!isLogged) return;
    this.loadCategories();
    this.loadUsers();
    this.fetch();
    this.simulatorService.getPublicConfig().subscribe({
      next: (cfg) => {
        this.publicConfig = cfg;
        this.applyPriceGuardsForAllKits();
        this.cdr.markForCheck();
      }
    });
  }

  // =========================================================
  // Data loading
  // =========================================================

  private fetch(): void {
    this.isLoading = true;
    this.service.getBookForEdit(this.idBookVersion).subscribe({
      next: (res: AdminBookEditorEditResponse) => {
        if (res.isError || !res.bookVersion) {
          this.alert.show('Erro', res.errorDescription || 'Não foi possível carregar o livro.', AlertType.error);
          this.isLoading = false;
          this.cdr.markForCheck();
          return;
        }

        const bv = res.bookVersion;
        this.bookVersion = bv;

        // Store existing URLs
        this.existingFrontCoverUrl = bv.bookFrontCoverFileName ?? null;
        this.existingBackCoverUrl = bv.bookBackCoverFileName ?? null;
        this.existingAuthorPhotoUrl = bv.authorPhotoFileName ?? null;
        this.existingCoverForPrintUrl = bv.bookAssemblyCoverFileName ?? null;
        this.existingBookForPrintUrl = bv.bookPrintFileName ?? null;
        this.existingEbookUrl = bv.bookReviewedFileName ?? null;

        // Pre-populate image previews from existing URLs so covers render immediately
        this.frontCoverPreview = this.existingFrontCoverUrl;
        this.backCoverPreview = this.existingBackCoverUrl;

        // Availability from existing data
        this.hasEbookPdf = !!(bv.bookReviewedFileName);
        this.hasPrintPdfs = !!(bv.bookAssemblyCoverFileName && bv.bookPrintFileName);

        // Patch form
        this.model.patchValue({
          bookTitle: bv.bookTitle ?? '',
          coverAuthorName: bv.coverAuthorName ?? '',
          bookSubtitle: bv.bookSubtitle ?? '',
          versionName: bv.versionName ?? '',
          collectionName: bv.collectionName ?? '',
          volumeName: bv.volumeName ?? '',
          synopsis: bv.synopsis ?? '',
          preface: bv.preface ?? '',
          backCoverText: bv.backCoverText ?? '',
          isbn: bv.isbn ?? '',
          doi: bv.doi ?? '',
          idPrimaryCategory: bv.idPrimaryCategory ?? null,
          idSecondaryCategory: bv.idSecondaryCategory ?? null,
          idPhotoShape: bv.idPhotoShape ?? 1,
          authorBio: bv.authorBio ?? '',
          totalPages: bv.bookEstimatedPages ?? 0,
          idBookPrint: res.idBookPrint ?? null,
        });

        // Set author photo preview from existing URL
        if (bv.authorPhotoFileName) {
          this.authorPhotoPreview = bv.authorPhotoFileName;
        }

        // Load prints for the existing page count
        if (bv.bookEstimatedPages && bv.bookEstimatedPages > 0) {
          this.loadBookPrints(bv.bookEstimatedPages);
        }

        // Co-authors — rebuild FormArray from backend data
        const authorsArr = this.model.get('listAuthors') as FormArray;
        while (authorsArr.length) authorsArr.removeAt(0);
        const existingAuthors = res.listAuthors ?? [];
        if (existingAuthors.length > 0) {
          existingAuthors.forEach(name =>
            authorsArr.push(this.fb.control(name || '', Validators.required))
          );
        } else {
          authorsArr.push(this.fb.control('', Validators.required));
        }

        // Book owner — pre-select the current user in the picker
        if (res.idUser && res.userName) {
          this.selectedUser = {
            idUser: res.idUser,
            userName: res.userName ?? '',
            userEmail: res.userEmail ?? '',
            isActive: true,
          };
        }

        // Status
        this.listStatusVersion = res.listStatusVersion ?? [];
        this.selectedStatusId = res.idStatusVersion ?? null;
        this.originalStatusId = res.idStatusVersion ?? null;

        // Map existing kits
        this.kitRows = (res.kits ?? []).map(k => {
          const items: string[] = [];
          if (k.includesBook) items.push('book');
          if (k.includesEbook) items.push('ebook');
          if (k.includesVideoAccess) items.push('video');
          return {
            idKit: k.idKit,
            kitName: k.kitName,
            kitDescription: k.kitDescription,
            totalPrice: Number(k.totalPrice) || 10,
            isActive: k.isActive,
            selectedItems: items,
          };
        });

        if (this.kitRows.length === 0) {
          this.kitRows = [this.newKitRow()];
        }

        // Marketplace: toggle, anúncios, log e bloqueio de edição
        this.applyMarketplaceState(res.marketplace, res.isLockedByMarketplace, res.marketplaceLockReason);

        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.alert.show('Erro', 'Falha ao carregar dados do livro.', AlertType.error);
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  private loadUsers(): void {
    this.userService.listActive(true).subscribe({
      next: (res: any) => {
        if (res?.isError) return;
        this.allUsers = (res?.listUser ?? []).filter((u: UserSimpleStruct) => u.isActive);
        this.applyUserFilter(this.userSearch);
        this.cdr.markForCheck();
      },
      error: () => this.alert.show('Erro', 'Erro ao carregar usuários.', AlertType.error),
    });
  }

  onUserSearch(value: string): void {
    this.userSearch = value;
    this.applyUserFilter(value);
  }

  private applyUserFilter(value: string): void {
    const v = (value || '').toLowerCase();
    this.filteredUsers = this.allUsers.filter(u =>
      !this.selectedUser || u.idUser !== this.selectedUser.idUser
    ).filter(u =>
      (u.userName?.toLowerCase().includes(v) || u.userEmail?.toLowerCase().includes(v))
    );
    this.cdr.markForCheck();
  }

  onUserSelected(u: UserSimpleStruct): void {
    if (this.isLockedByMarketplace) return; // livro sincronizado com marketplace
    this.selectedUser = u;
    this.userSearch = '';
    this.applyUserFilter('');
    this.cdr.markForCheck();
  }

  clearUser(): void {
    if (this.lockUserSelect) return;
    this.selectedUser = null;
    this.userSearch = '';
    this.applyUserFilter('');
    this.cdr.markForCheck();
  }

  openCreateUserModal(): void {
    const ref = this.dialog.open(UserRegisterComponent, {
      data: { isAdminFlow: true }
    });
    ref.afterClosed().subscribe((created: boolean) => {
      if (created) this.loadUsers();
    });
  }

  private loadCategories(): void {
    this.categoryService.getCategory().subscribe({
      next: (res: any) => {
        if (res?.isError) {
          this.alert.show('Erro', res.errorDescription || 'Falha ao carregar categorias.', AlertType.error);
          return;
        }
        this.listCategories = res.listCategoryStruct ?? [];
        this.buildAllOptionsSorted();
        this.refreshCategoryOptions();
        this.cdr.markForCheck();
      },
      error: () => this.alert.show('Erro', 'Erro ao carregar categorias.', AlertType.error),
    });
  }

  loadBookPrints(totalPages: number): void {
    this.bookPrintService.getActiveByNumPages(totalPages).subscribe({
      next: (res: any) => {
        if (res?.isError) return;
        this.listBookPrint = res.listBookPrintStruct ?? res.listBookPrint ?? [];

        // If the currently selected print is no longer available for this page count, clear it
        const currentId = Number(this.model?.value?.idBookPrint || 0);
        const stillAvailable = this.listBookPrint.some((p: any) => Number(p?.idBookPrint) === currentId);
        if (currentId > 0 && !stillAvailable) {
          this.model.patchValue({ idBookPrint: null });
        }

        // rawUnitCost changed for the new page count — re-evaluate all kit minimums
        this.applyPriceGuardsForAllKits();
        this.cdr.markForCheck();
      },
    });
  }

  private applyPriceGuardsForAllKits(): void {
    for (const kit of this.kitRows) {
      this.applyPriceGuards(kit);
    }
  }

  // =========================================================
  // Category helpers
  // =========================================================

  private buildAllOptionsSorted(): void {
    const byClass = new Map<number, { className: string; items: CategoryStruct[] }>();
    for (const c of this.listCategories) {
      if (!byClass.has(c.idCategoryClass)) {
        byClass.set(c.idCategoryClass, { className: c.categoryClassName, items: [] });
      }
      byClass.get(c.idCategoryClass)!.items.push(c);
    }
    const classes = [...byClass.entries()]
      .map(([classKey, v]) => ({
        classKey,
        className: v.className,
        items: v.items.sort((a, b) => a.categoryName.localeCompare(b.categoryName, 'pt-BR')),
      }))
      .sort((a, b) => a.className.localeCompare(b.className, 'pt-BR'));

    const result: CategoryOptionItem[] = [];
    for (const g of classes) {
      result.push({ isHeader: true, label: g.className, classKey: g.classKey });
      for (const it of g.items) {
        result.push({ isHeader: false, label: it.categoryName, idCategory: it.idCategory, classKey: g.classKey });
      }
    }
    this.allOptions = result;
  }

  private buildFilteredOptions(excludeId: number | null): CategoryOptionItem[] {
    const out: CategoryOptionItem[] = [];
    let headerIdx: number | null = null;
    let headerHasItems = false;

    const flush = () => {
      if (headerIdx != null && !headerHasItems) out.splice(headerIdx, 1);
      headerIdx = null;
      headerHasItems = false;
    };

    for (const opt of this.allOptions) {
      if (opt.isHeader) {
        flush();
        out.push(opt);
        headerIdx = out.length - 1;
        headerHasItems = false;
      } else {
        if (excludeId && opt.idCategory === Number(excludeId)) continue;
        out.push(opt);
        headerHasItems = true;
      }
    }
    flush();
    return out;
  }

  private refreshCategoryOptions(): void {
    const primary = this.model?.get('idPrimaryCategory')?.value ?? null;
    const secondary = this.model?.get('idSecondaryCategory')?.value ?? null;
    this.primaryOptions = this.buildFilteredOptions(secondary ? Number(secondary) : null);
    this.secondaryOptions = this.buildFilteredOptions(primary ? Number(primary) : null);
    this.cdr.markForCheck();
  }

  trackByOption = (_: number, item: CategoryOptionItem) =>
    item.isHeader ? `h-${item.classKey}-${item.label}` : `i-${item.idCategory}`;

  trackByPrint = (_: number, p: any) => p?.idBookPrint ?? _;

  trackByStatus = (_: number, s: StatusVersionItem) => s.idStatusVersion;

  get hasActiveKitWithItems(): boolean {
    return this.kitRows.some(k => k.isActive && k.selectedItems.length > 0);
  }

  get publishedStatusBlocked(): boolean {
    return this.selectedStatusId === this.PUBLISHED_STATUS_ID && !this.hasActiveKitWithItems;
  }

  selectBookPrint(id: number): void {
    if (this.isLockedByMarketplace) return; // livro sincronizado com marketplace
    this.model.patchValue({ idBookPrint: id });
    // Different print = different rawUnitCost → recalc all kit minimums
    this.applyPriceGuardsForAllKits();
    this.cdr.markForCheck();
  }

  // =========================================================
  // Availability
  // =========================================================

  private updateAvailability(): void {
    // A file is available if EITHER a new upload exists OR an existing backend URL remains
    const coverNew = String(this.model.value?.coverForPrintFileName || '');
    const bookNew = String(this.model.value?.bookForPrintFileName || '');
    const ebookNew = String(this.model.value?.bookForEbookFileName || '');

    this.hasPrintPdfs =
      (coverNew.length > 0 || !!this.existingCoverForPrintUrl) &&
      (bookNew.length > 0 || !!this.existingBookForPrintUrl);

    this.hasEbookPdf = ebookNew.length > 0 || !!this.existingEbookUrl;

    // Remove disallowed items from all kits
    for (const kit of this.kitRows) {
      if (!this.hasPrintPdfs) kit.selectedItems = kit.selectedItems.filter(x => x !== 'book');
      if (!this.hasEbookPdf) kit.selectedItems = kit.selectedItems.filter(x => x !== 'ebook');
    }
    this.cdr.markForCheck();
  }

  // =========================================================
  // Kit helpers
  // =========================================================

  private newKitRow(): KitEditRow {
    return { idKit: 0, kitName: '', kitDescription: '', totalPrice: 10, isActive: true, selectedItems: [] };
  }

  addKit(): void {
    if (this.isLockedByMarketplace) return; // livro sincronizado com marketplace
    this.kitRows.push(this.newKitRow());
    this.cdr.markForCheck();
  }

  removeKit(i: number): void {
    if (this.isLockedByMarketplace) return; // livro sincronizado com marketplace
    if (this.kitRows.length <= 1) return;
    this.kitRows.splice(i, 1);
    this.cdr.markForCheck();
  }

  applyPriceGuards(kit: KitEditRow): void {
    const min = this.getMinPriceForKit(kit);
    const val = Number(kit.totalPrice ?? 0);
    if (isNaN(val) || val < min) kit.totalPrice = min;
    this.cdr.markForCheck();
  }

  getMinPriceForKit(kit: KitEditRow): number {
    const hasPhysical = kit.selectedItems?.includes('book') ?? false;
    const id = Number(this.model?.value?.idBookPrint || 0);
    const print = this.listBookPrint.find((p: any) => Number(p?.idBookPrint) === id);
    const calc: BookCostCalc = {
      unitCost: hasPhysical ? Number(print?.estimatedTotalCost ?? 0) : 0,
      platformProfitPercent: this.publicConfig?.platformProfitPercent ?? 0,
      baseTaxPercent: this.publicConfig?.baseTaxPercent ?? 0,
    };
    return getMinPrice(calc, hasPhysical, 10);
  }

  formatCurrency(value: number | null): string {
    if (value == null) return '';
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });
  }

  onCurrencyInput(event: Event, kit: KitEditRow): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value.replace(/\D/g, '');
    const numeric = parseFloat(raw) / 100;
    kit.totalPrice = isNaN(numeric) ? 0 : Number(numeric.toFixed(2));
    input.value = this.formatCurrency(kit.totalPrice);
    this.cdr.markForCheck();
  }

  onCurrencyBlur(kit: KitEditRow): void {
    if (!kit.totalPrice) kit.totalPrice = 0;
    this.applyPriceGuards(kit);
  }

  // =========================================================
  // Upload helpers
  // =========================================================

  onDragOver(ev: DragEvent) { ev.preventDefault(); }

  private ensureImageLimit(file: File): boolean {
    if (file.size <= AdminBookEditorEditComponent.MAX_IMAGE_BYTES) return true;
    this.alert.show('Imagem muito grande', 'Limite: 5 MB.', AlertType.warning);
    return false;
  }

  private ensurePdfLimit(file: File): boolean {
    if (file.size <= AdminBookEditorEditComponent.MAX_PDF_BYTES) return true;
    this.alert.show('PDF muito grande', `Arquivo com ${(file.size / (1024 * 1024)).toFixed(1)} MB. Limite: ~210 MB.`, AlertType.warning);
    return false;
  }

  private setUploadLoading(key: string, val: boolean): void {
    this.uploadLoading[key] = val;
    this.cdr.markForCheck();
  }

  private hasPendingUploads(): boolean {
    return Object.values(this.uploadLoading).some(v => v === true);
  }

  private async fileToDataUrl(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Falha ao ler arquivo'));
      reader.readAsDataURL(file);
    });
  }

  private async uploadByPurpose(file: File, purpose: UploadPurposeEnum): Promise<FileUploadFinalizeResponse> {
    const createRes = await new Promise<FileUploadCreateResponse>((resolve, reject) => {
      this.fileUpload.create({
        originalFileName: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        purpose: purpose,
      }).subscribe({ next: (x: any) => resolve(x as FileUploadCreateResponse), error: (e) => reject(e) });
    });

    if (!createRes?.idFileUploadSession || !createRes?.uploadUrl) {
      throw new Error('CreateUpload inválido (sem uploadId/url).');
    }

    const headers = new Headers();
    const extraHeaders: Record<string, string> | null | undefined = (createRes as any).headers ?? null;
    if (extraHeaders) {
      for (const k of Object.keys(extraHeaders)) {
        const v = extraHeaders[k];
        if (v != null) headers.set(k, String(v));
      }
    }
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', file.type || 'application/octet-stream');
    }

    const putResp = await fetch(createRes.uploadUrl, { method: 'PUT', headers, body: file });
    if (!putResp.ok) {
      const msg = await putResp.text().catch(() => '');
      throw new Error(`Falha no PUT (${putResp.status}). ${msg || ''}`.trim());
    }

    const fin = await new Promise<FileUploadFinalizeResponse>((resolve, reject) => {
      this.fileUpload.finalize({ idFileUploadSession: createRes.idFileUploadSession })
        .subscribe({ next: (x) => resolve(x as FileUploadFinalizeResponse), error: (e) => reject(e) });
    });

    if (!fin?.fileName) throw new Error('FinalizeUpload inválido (sem fileName).');
    return fin;
  }

  // ---- Author photo ----
  onAuthorPhotoDrop(ev: DragEvent): void {
    if (this.isLockedByMarketplace) return; // livro sincronizado com marketplace
    ev.preventDefault();
    ev.stopPropagation();
    const file = ev.dataTransfer?.files?.[0];
    if (file) this.handleAuthorPhotoFile(file);
  }

  onAuthorPhotoSelected(ev: Event): void {
    if (this.isLockedByMarketplace) return; // livro sincronizado com marketplace
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.handleAuthorPhotoFile(file);
    input.value = '';
  }

  private async handleAuthorPhotoFile(file: File): Promise<void> {
    if (!this.ensureImageLimit(file)) return;
    this.setUploadLoading('authorPhoto', true);
    try {
      const dataUrl = await this.fileToDataUrl(file);
      this.zone.run(() => { this.authorPhotoPreview = dataUrl; this.cdr.markForCheck(); });
      const fin = await this.uploadByPurpose(file, UploadPurposeEnum.AuthorPhoto);
      this.zone.run(() => {
        this.model.patchValue({ authorPhotoFileName: fin.fileName });
        this.setUploadLoading('authorPhoto', false);
      });
    } catch (e: any) {
      this.zone.run(() => {
        this.alert.show('Erro', e?.message || 'Falha ao enviar a foto do autor.', AlertType.error);
        this.setUploadLoading('authorPhoto', false);
      });
    }
  }

  clearAuthorPhoto(): void {
    if (this.isLockedByMarketplace) return; // livro sincronizado com marketplace
    this.authorPhotoPreview = null;
    this.existingAuthorPhotoUrl = null;
    this.model.patchValue({ authorPhotoFileName: '' });
    this.cdr.markForCheck();
  }

  // ---- Front / Back cover ----
  onFrontCoverSelected(ev: Event): void { this.handleImagePick(ev, 'front'); }
  onBackCoverSelected(ev: Event): void { this.handleImagePick(ev, 'back'); }

  private async handleImagePick(ev: Event, which: 'front' | 'back'): Promise<void> {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (!this.ensureImageLimit(file)) { input.value = ''; return; }

    const key = which === 'front' ? 'frontCover' : 'backCover';
    this.setUploadLoading(key, true);
    try {
      const dataUrl = await this.fileToDataUrl(file);
      this.zone.run(() => {
        if (which === 'front') this.frontCoverPreview = dataUrl;
        else this.backCoverPreview = dataUrl;
        this.cdr.markForCheck();
      });
      const fin = await this.uploadByPurpose(file, which === 'front' ? UploadPurposeEnum.FrontCover : UploadPurposeEnum.BackCover);
      this.zone.run(() => {
        if (which === 'front') this.model.patchValue({ frontCoverFileName: fin.fileName });
        else this.model.patchValue({ backCoverFileName: fin.fileName });
        this.setUploadLoading(key, false);
      });
    } catch (e: any) {
      this.zone.run(() => {
        this.alert.show('Erro', e?.message || 'Falha ao enviar imagem.', AlertType.error);
        this.setUploadLoading(key, false);
      });
    } finally {
      input.value = '';
    }
  }

  clearFrontCover(): void {
    if (this.isLockedByMarketplace) return; // livro sincronizado com marketplace
    this.frontCoverPreview = null;
    this.existingFrontCoverUrl = null;
    this.model.patchValue({ frontCoverFileName: '' });
    this.cdr.markForCheck();
  }

  clearBackCover(): void {
    if (this.isLockedByMarketplace) return; // livro sincronizado com marketplace
    this.backCoverPreview = null;
    this.existingBackCoverUrl = null;
    this.model.patchValue({ backCoverFileName: '' });
    this.cdr.markForCheck();
  }

  // ---- PDFs ----
  onCoverForPrintSelected(ev: Event): void { this.handlePdfPick(ev, 'coverForPrintLabel', UploadPurposeEnum.CoverForPrintPdf); }
  onBookForPrintSelected(ev: Event): void { this.handlePdfPick(ev, 'bookForPrintLabel', UploadPurposeEnum.BookForPrintPdf); }
  onBookForEbookSelected(ev: Event): void { this.handlePdfPick(ev, 'bookForEbookLabel', UploadPurposeEnum.BookForEbookPdf); }

  private async handlePdfPick(
    ev: Event,
    labelField: 'coverForPrintLabel' | 'bookForPrintLabel' | 'bookForEbookLabel',
    purpose: UploadPurposeEnum,
  ): Promise<void> {
    if (this.isLockedByMarketplace) { (ev.target as HTMLInputElement).value = ''; return; }
    if (this.isLockedByMarketplace) { (ev.target as HTMLInputElement).value = ''; return; }
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (!this.ensurePdfLimit(file)) { input.value = ''; return; }

    const key = labelField === 'coverForPrintLabel' ? 'coverForPrint'
      : labelField === 'bookForPrintLabel' ? 'bookForPrint'
      : 'bookForEbook';

    this.setUploadLoading(key, true);
    try {
      const fin = await this.uploadByPurpose(file, purpose);
      this.zone.run(() => {
        if (labelField === 'coverForPrintLabel') this.model.patchValue({ coverForPrintFileName: fin.fileName });
        if (labelField === 'bookForPrintLabel') this.model.patchValue({ bookForPrintFileName: fin.fileName });
        if (labelField === 'bookForEbookLabel') this.model.patchValue({ bookForEbookFileName: fin.fileName });
        (this as any)[labelField] = `${file.name} (${(file.size / (1024 * 1024)).toFixed(1)} MB)`;
        this.updateAvailability();
        this.setUploadLoading(key, false);
      });
    } catch (e: any) {
      this.zone.run(() => {
        this.alert.show('Erro', e?.message || 'Falha ao enviar PDF.', AlertType.error);
        this.setUploadLoading(key, false);
      });
    } finally {
      input.value = '';
    }
  }

  clearCoverForPrint(): void {
    if (this.isLockedByMarketplace) return; // livro sincronizado com marketplace
    this.coverForPrintLabel = null;
    this.existingCoverForPrintUrl = null;
    this.model.patchValue({ coverForPrintFileName: '' });
    this.updateAvailability();
  }

  clearBookForPrint(): void {
    if (this.isLockedByMarketplace) return; // livro sincronizado com marketplace
    this.bookForPrintLabel = null;
    this.existingBookForPrintUrl = null;
    this.model.patchValue({ bookForPrintFileName: '' });
    this.updateAvailability();
  }

  clearBookForEbook(): void {
    if (this.isLockedByMarketplace) return; // livro sincronizado com marketplace
    this.bookForEbookLabel = null;
    this.existingEbookUrl = null;
    this.model.patchValue({ bookForEbookFileName: '' });
    this.updateAvailability();
  }

  // =========================================================
  // ISBN validator
  // =========================================================
  isbnValidator(ctrl: AbstractControl): ValidationErrors | null {
    const raw = ((ctrl.value as string) || '').replace(/[^0-9Xx]/g, '');
    if (!raw) return { required: true };
    if (!(raw.length === 10 || raw.length === 13)) return { isbn: true };
    return null;
  }

  // =========================================================
  // Save
  // =========================================================
  save(): void {
    if (this.isSaving) return;

    // Com o formulário desabilitado, model.invalid é false — o bloqueio precisa ser checado antes.
    if (this.isLockedByMarketplace) {
      this.alert.show('Livro bloqueado para edição', this.marketplaceLockReason || 'Livro sincronizado com marketplace.', AlertType.warning);
      return;
    }

    if (this.hasPendingUploads()) {
      this.alert.show('Aguarde', 'Existem arquivos ainda sendo enviados. Aguarde terminar antes de salvar.', AlertType.warning);
      return;
    }

    if (this.model.invalid) {
      this.model.markAllAsTouched();
      this.alert.show('Erro', 'Existem campos obrigatórios inválidos.', AlertType.error);
      return;
    }

    if (this.publishedStatusBlocked) {
      this.alert.show(
        'Kit obrigatório para Publicado',
        'Para definir o status como Publicado, o livro deve ter ao menos um kit ativo com itens selecionados.',
        AlertType.warning
      );
      return;
    }

    const v = this.model.value;
    const req = new AdminBookEditorSaveRequest();
    req.idBookVersion = this.idBookVersion;
    req.bookTitle = v.bookTitle || null;
    req.coverAuthorName = v.coverAuthorName || null;
    req.bookSubtitle = v.bookSubtitle || null;
    req.versionName = v.versionName || null;
    req.collectionName = v.collectionName || null;
    req.volumeName = v.volumeName || null;
    req.synopsis = v.synopsis || null;
    req.preface = v.preface || null;
    req.backCoverText = v.backCoverText || null;
    req.isbn = v.isbn || null;
    req.doi = v.doi || null;
    req.idPrimaryCategory = v.idPrimaryCategory ?? null;
    req.idSecondaryCategory = v.idSecondaryCategory ?? null;
    req.idPhotoShape = v.idPhotoShape ?? null;
    req.authorBio = v.authorBio || null;
    req.totalPages = Number(v.totalPages) || null;
    req.idBookPrint = Number(v.idBookPrint) || null;
    req.authorPhotoFileName = v.authorPhotoFileName || null;
    req.frontCoverFileName = v.frontCoverFileName || null;
    req.backCoverFileName = v.backCoverFileName || null;
    req.coverForPrintFileName = v.coverForPrintFileName || null;
    req.bookForPrintFileName = v.bookForPrintFileName || null;
    req.bookForEbookFileName = v.bookForEbookFileName || null;
    req.saleCoverFileName = v.saleCoverFileName || null;
    req.idStatusVersion = this.selectedStatusId ?? null;
    req.idUserAuthor = this.selectedUser?.idUser ?? null;
    req.listAuthors = (this.authors.value as string[])
      .map(n => (n || '').trim())
      .filter(n => n.length > 0);

    // Map kit rows → request, resolving selectedItems → booleans
    req.kits = this.kitRows.map(kit => {
      const k = new AdminBookEditorKitSaveRequest();
      k.idKit = kit.idKit;
      k.kitName = kit.kitName;
      k.kitDescription = kit.kitDescription;
      k.totalPrice = Number(kit.totalPrice) || 10;
      k.includesBook = kit.selectedItems?.includes('book') || false;
      k.includesEbook = kit.selectedItems?.includes('ebook') || false;
      k.includesVideoAccess = kit.selectedItems?.includes('video') || false;
      k.isActive = kit.isActive;
      return k;
    });

    this.isSaving = true;
    this.cdr.markForCheck();

    this.service.saveEdit(req).subscribe({
      next: (res: any) => {
        if (res?.isError) {
          this.alert.show('Erro', res.errorDescription || 'Falha ao salvar.', AlertType.error);
        } else {
          this.alert.show('Salvo', 'Alterações aplicadas com sucesso.', AlertType.success);
          // Kits imutáveis: o backend pode ter criado novos kits (novo idKit) e desativado
          // os antigos. Recarrega para atualizar os idKit e evitar perda de edição num
          // segundo save na mesma sessão.
          this.fetch();
        }
        this.isSaving = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.alert.show('Erro', 'Falha ao salvar alterações.', AlertType.error);
        this.isSaving = false;
        this.cdr.markForCheck();
      },
    });
  }

  goBack(): void {
    this.router.navigateByUrl('/editor-admin');
  }

  // =========================================================
  // Marketplace (Amazon)
  // =========================================================

  /** Aplica o estado de marketplace vindo da API e (des)bloqueia o formulário. */
  private applyMarketplaceState(
    marketplace: AdminBookEditorMarketplaceStruct | null,
    isLocked: boolean,
    lockReason: string | null,
  ): void {
    this.marketplace = marketplace;
    this.isLockedByMarketplace = !!isLocked;
    this.marketplaceLockReason = lockReason ?? null;

    if (this.isLockedByMarketplace) {
      this.model.disable({ emitEvent: false });
    } else {
      this.model.enable({ emitEvent: false });
    }
    this.lockUserSelect = this.isLockedByMarketplace;
    this.cdr.markForCheck();
  }

  onToggleMarketplace(ev: MatSlideToggleChange): void {
    const enabled = ev.checked;
    const title = enabled ? 'Liberar para marketplace' : 'Desativar publicação no marketplace';
    const description = enabled
      ? `O livro será publicado na Amazon com o preço do kit "${this.marketplace?.selectedKit?.kitName ?? ''}" ` +
        `(${this.formatCurrency(this.marketplace?.selectedKit?.totalPrice ?? null)}). ` +
        'A partir deste momento os dados do livro ficam bloqueados para edição. Deseja continuar?'
      : 'O anúncio será removido do marketplace. A edição do livro será liberada assim que a integração ' +
        'concluir a remoção. Deseja continuar?';

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: { title, description, buttonConfirm: enabled ? 'Sim, liberar' : 'Sim, desativar', buttonClose: 'Cancelar' },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result?.confirm) {
        ev.source.checked = !enabled; // desfaz o toggle visualmente
        this.cdr.markForCheck();
        return;
      }

      const req = new AdminBookEditorMarketplaceToggleRequest();
      req.idBookVersion = this.idBookVersion;
      req.enabled = enabled;

      this.isTogglingMarketplace = true;
      this.cdr.markForCheck();

      this.service.toggleMarketplace(req).subscribe({
        next: (res: AdminBookEditorMarketplaceResponse) => this.handleMarketplaceResponse(res, () => (ev.source.checked = !enabled)),
        error: () => {
          ev.source.checked = !enabled;
          this.alert.show('Erro', 'Falha ao alterar a publicação no marketplace.', AlertType.error);
          this.isTogglingMarketplace = false;
          this.cdr.markForCheck();
        },
      });
    });
  }

  reprocessListing(listing: MarketplaceListingStruct): void {
    const req = new AdminBookEditorMarketplaceReprocessRequest();
    req.idBookVersion = this.idBookVersion;
    req.idMarketplace = listing.idMarketplace;

    this.isTogglingMarketplace = true;
    this.cdr.markForCheck();

    this.service.reprocessMarketplace(req).subscribe({
      next: (res: AdminBookEditorMarketplaceResponse) => this.handleMarketplaceResponse(res),
      error: () => {
        this.alert.show('Erro', 'Falha ao solicitar o reprocessamento.', AlertType.error);
        this.isTogglingMarketplace = false;
        this.cdr.markForCheck();
      },
    });
  }

  private handleMarketplaceResponse(res: AdminBookEditorMarketplaceResponse, onError?: () => void): void {
    this.isTogglingMarketplace = false;
    if (res?.isError) {
      onError?.();
      this.alert.show('Marketplace', res.errorDescription || 'Não foi possível concluir a operação.', AlertType.error);
      this.cdr.markForCheck();
      return;
    }
    this.applyMarketplaceState(res.marketplace, res.isLockedByMarketplace, res.marketplaceLockReason);
    this.alert.show('Marketplace', res.message || 'Operação realizada.', AlertType.success);
  }

  listingChipClass(l: MarketplaceListingStruct): string {
    switch (l.idListingStatus) {
      case MarketplaceListingStatusEnum.Publicado: return 'chip chip-success';
      case MarketplaceListingStatusEnum.Erro: return 'chip chip-warn';
      case MarketplaceListingStatusEnum.Removido: return 'chip chip-off';
      case MarketplaceListingStatusEnum.Publicando: return 'chip chip-info';
      default: return 'chip chip-pending';
    }
  }

  logChipClass(l: MarketplaceSyncLogStruct): string {
    switch (l.idSyncStatus) {
      case MarketplaceSyncStatusEnum.Success: return 'chip chip-success';
      case MarketplaceSyncStatusEnum.Error: return 'chip chip-warn';
      default: return 'chip chip-info';
    }
  }

  toggleLogPayload(l: MarketplaceSyncLogStruct): void {
    this.expandedLogId = this.expandedLogId === l.idMarketplaceSyncLog ? null : l.idMarketplaceSyncLog;
    this.cdr.markForCheck();
  }

  hasPayload(l: MarketplaceSyncLogStruct): boolean {
    return !!(l.requestPayload || l.responsePayload);
  }

  // =========================================================
  // Co-authors (FormArray)
  // =========================================================

  get authors(): FormArray {
    return this.model.get('listAuthors') as FormArray;
  }

  addAuthor(): void {
    if (this.isLockedByMarketplace) return; // livro sincronizado com marketplace
    this.authors.push(this.fb.control('', Validators.required));
    this.cdr.markForCheck();
  }

  removeAuthor(index: number): void {
    if (this.isLockedByMarketplace) return; // livro sincronizado com marketplace
    if (this.authors.length > 1) this.authors.removeAt(index);
    this.cdr.markForCheck();
  }
}
